import React from "react";
import { twClassNames } from "../../helpers/twClassNames";
import { Chip } from "../Chip";
import { TooltipContainer } from "../Tooltip.jsx";

const toPercent = (value: number): string => (value * 100).toFixed(value === 1 ? 0 : 1);

const ProgressBarColorScheme = {
    default: {
        containerClassName: "bg-white ring-1 ring-gray-400",
        barClassName: "bg-gray-200 ring-1 ring-offset-0 ring-gray-600"
    },
    purboflow: {
        containerClassName: "bg-white ring-1 ring-gray-400",
        barClassName: "bg-purple-200 ring-1 ring-offset-0 ring-purple-600"
    },
    purboflowBrand: {
        containerClassName: "bg-white ring-1 ring-gray-400",
        barClassName: "bg-purple-300 ring-1 ring-offset-0 ring-purple-600"
    },
    lightBlue: {
        containerClassName: "bg-white ring-1 ring-gray-400",
        barClassName: "bg-light-blue-200 ring-1 ring-offset-0 ring-light-blue-600"
    },
    yellow: {
        containerClassName: "bg-white ring-1 ring-gray-400",
        barClassName: "bg-yellow-200 ring-1 ring-offset-0 ring-yellow-600"
    }
};

export function ProgressBar({
    value,
    colorScheme,
    length,
    layout = "horizontal",
    progressBarClassName
}: {
    value: number;
    colorScheme: keyof typeof ProgressBarColorScheme;
    length?: string;
    layout?: "horizontal" | "vertical";
    progressBarClassName?: string;
}) {
    const { containerClassName, barClassName } = ProgressBarColorScheme[colorScheme];
    const isVertical = layout === "vertical";

    return (
        <div
            className={twClassNames(
                "flex-shrink-0 rounded-full",
                containerClassName,
                length ? length : isVertical ? "h-9" : "w-14 flex-1",
                isVertical ? "w-2" : "h-1.5",
                progressBarClassName
            )}
        >
            {value > 0 && (
                <div
                    className={twClassNames("h-full rounded-full", barClassName)}
                    style={
                        isVertical
                            ? {
                                  height: `${toPercent(value)}%`,
                                  position: "absolute",
                                  bottom: 0,
                                  width: "100%"
                              }
                            : { width: `${toPercent(value)}%` }
                    }
                ></div>
            )}
        </div>
    );
}

export function Metric({
    label,
    value,
    colorScheme,
    valueLabel,
    valueLabelClassName,
    layout = "horizontal",
    className,
    progressBarClassName,
    progressBarAndLabelClassName
}: {
    label: string;
    value: number;
    valueLabel?: string;
    valueLabelClassName?: string;
    colorScheme: keyof typeof ProgressBarColorScheme;
    layout?: "horizontal" | "vertical";
    className?: string;
    progressBarClassName?: string;
    progressBarAndLabelClassName?: string;
}) {
    if (layout === "vertical") {
        return (
            <div className={twClassNames("flex items-center gap-x-3", className)}>
                <div className="relative h-9 w-2">
                    <ProgressBar
                        value={value}
                        colorScheme={colorScheme}
                        layout="vertical"
                        progressBarClassName={progressBarClassName}
                    />
                </div>
                <div className={twClassNames("flex flex-col gap-2", progressBarAndLabelClassName)}>
                    <div className="text-[13px] text-gray-700">{label}</div>
                    <div
                        className={twClassNames(
                            "w-10 truncate font-mono text-xs font-medium text-gray-900",
                            valueLabelClassName
                        )}
                        title={valueLabel}
                    >
                        {valueLabel || toPercent(value) + "%"}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={twClassNames("flex items-center gap-x-1 xl:gap-x-2", className)}>
            <div className="min-w-16 text-[13px] text-gray-700">{label}</div>
            <div
                className={twClassNames(
                    "flex flex-1 items-center gap-x-1",
                    progressBarAndLabelClassName
                )}
            >
                <div
                    className={twClassNames(
                        "w-11 truncate pr-1 text-right font-mono text-xs font-medium text-gray-900",
                        valueLabelClassName
                    )}
                    title={valueLabel}
                >
                    {valueLabel || toPercent(value) + "%"}
                </div>
                <ProgressBar
                    value={value}
                    colorScheme={colorScheme}
                    layout="horizontal"
                    progressBarClassName={progressBarClassName}
                />
            </div>
        </div>
    );
}

function calculatePerplexityScale(perplexity: number): number {
    return Math.pow(0.95, perplexity - 1);
}

//defend agains string values that are also numbers
function toNumber(value?: number | string): number {
    if (typeof value === "string") {
        const num = Number(value);
        return isNaN(num) ? 0 : num;
    }
    return Number(value);
}

function isNumber(value?: number | string): boolean {
    if (typeof value === "string") {
        const num = Number(value);
        return !isNaN(num);
    }
    return typeof value === "number";
}

export function ModelTrainMetricsTd({
    results,
    layout = "horizontal",
    modelType,
    rfdetrHasRealPrecisionRecall,
    datasetType
}: {
    results?: {
        accuracy?: number | string;
        map?: number | string;
        precision?: number | string;
        recall?: number | string;
        perplexity_parsed?: string;
        metrics?: { val_perplexity?: number | string };
        val_mean_iou?: number | string;
        hasRealPrecisionRecall?: boolean;
        val_perplexity?: number | string;
        val_loss?: number | string;
    };
    layout?: "horizontal" | "vertical";
    modelType?: string;
    rfdetrHasRealPrecisionRecall?: boolean;
    datasetType?: string;
}) {
    if (!results || Object.keys(results).length === 0) {
        return (
            <div className="h-[64px] w-auto lg:w-[262px]">
                <Chip className="h-full w-full items-center justify-center">
                    Train Metrics Not Available
                </Chip>
            </div>
        );
    }
    const isRfdetrModel = modelType?.toLowerCase().includes("rfdetr");
    const hasPrecision =
        isNumber(results.precision) && (!isRfdetrModel || rfdetrHasRealPrecisionRecall);
    const hasRecall = isNumber(results.recall) && (!isRfdetrModel || rfdetrHasRealPrecisionRecall);
    const valPerplexity = results?.metrics?.val_perplexity || results?.val_perplexity;
    const industryStandardMapEnabled =
        datasetType === "object-detection" || datasetType === "instance-segmentation";

    return (
        <div className="flex flex-col gap-1">
            {industryStandardMapEnabled && (
                <div className="flex items-center justify-between gap-1">
                    <div className="text-[10px] text-gray-500">Valid Set</div>
                    <div className="flex flex-row items-center gap-1">
                        <div className="text-[10px] text-gray-500">External</div>
                        <TooltipContainer
                            customButton={
                                <i className="far transiton fa-question-circle flex h-4 w-4 flex-none items-center justify-center rounded text-[10px] text-gray-500 duration-200 hover:bg-gray-300 hover:text-gray-800"></i>
                            }
                            type="dark"
                            placement="bottom"
                            titleOnly={true}
                            body={
                                <div>
                                    Provided by external model providers. For recently trained
                                    models, metrics calculations use industy standard methodology.{" "}
                                    <a
                                        className="explicit2 text-xs !text-purboflow-300"
                                        href="https://blog.roboflow.com/roboflow-verified-model-metrics/"
                                        target="_blank"
                                    >
                                        Learn More
                                    </a>
                                </div>
                            }
                        />
                    </div>
                </div>
            )}
            <div
                className={
                    layout === "vertical"
                        ? "flex w-full flex-nowrap gap-4 pt-1 md:w-min md:flex-wrap lg:flex-nowrap lg:gap-x-6"
                        : "-mt-1 space-y-0"
                }
            >
                {isNumber(results.accuracy) && (
                    <Metric
                        label="Accuracy"
                        value={toNumber(results.accuracy)}
                        colorScheme="purboflow"
                        layout={layout}
                    />
                )}
                {/* TODO: Fix that it can also be mAP because of the epochs data */}
                {isNumber(results.map || results.mAP) && (
                    <Metric
                        label="mAP@50"
                        value={toNumber(results.map || results.mAP)}
                        colorScheme={industryStandardMapEnabled ? "default" : "purboflow"}
                        layout={layout}
                    />
                )}
                {/* Only show precision if not an rfdetr model */}
                {hasPrecision && (
                    <Metric
                        label="Precision"
                        value={toNumber(results.precision)}
                        colorScheme={industryStandardMapEnabled ? "default" : "lightBlue"}
                        layout={layout}
                    />
                )}
                {/* Only show recall if not an rfdetr model */}
                {hasRecall && (
                    <Metric
                        label="Recall"
                        value={toNumber(results.recall)}
                        colorScheme={industryStandardMapEnabled ? "default" : "yellow"}
                        layout={layout}
                    />
                )}
                {isNumber(valPerplexity) && (
                    <Metric
                        label="Perplexity"
                        value={calculatePerplexityScale(toNumber(valPerplexity))}
                        colorScheme="purboflow"
                        valueLabel={valPerplexity?.toString()}
                        valueLabelClassName="w-[80px]"
                        layout={layout}
                    />
                )}
                {isNumber(results.val_mean_iou) && (
                    <Metric
                        label="mIoU"
                        value={toNumber(results.val_mean_iou)}
                        colorScheme="purboflow"
                        layout={layout}
                    />
                )}
            </div>
        </div>
    );
}
